import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "antd";
import { HiOutlineBellAlert, HiChevronRight, HiOutlineExclamationTriangle } from "react-icons/hi2";
import { question } from "@alert";
import { ALERT_TYPES, alertText, formatNumber } from "./branchContractFormat";
import { useBranchContractSummary } from "./useBranchContractSummary";
import "./branchContract.scss";

const SEVERITY = {
  [ALERT_TYPES.CONTRACT_EXPIRED]: "danger",
  [ALERT_TYPES.CONTRACT_EXPIRING]: "warn",
  [ALERT_TYPES.SMS_LOW]: "warn",
};

/** 簡訊點數已用完：左側顯示警告標誌 */
function isSmsEmptyAlert(alert) {
  return alert?.type === ALERT_TYPES.SMS_LOW && !(Number(alert.balance) > 0);
}

/** 數字加粗放大（如「剩 40 點」的 40） */
function AlertMessage({ alert }) {
  switch (alert?.type) {
    case ALERT_TYPES.CONTRACT_EXPIRED:
      return "合約已到期";
    case ALERT_TYPES.CONTRACT_EXPIRING:
      return (
        <>
          合約將於{" "}
          <strong className="branch-contract-alert__num tabular-nums">{formatNumber(alert.daysRemaining)}</strong>
          {" "}天後到期
        </>
      );
    case ALERT_TYPES.SMS_LOW:
      if (!(Number(alert.balance) > 0)) return "簡訊點數已用完";
      return (
        <>
          簡訊點數剩{" "}
          <strong className="branch-contract-alert__num tabular-nums">{formatNumber(alert.balance)}</strong>
          {" "}點
        </>
      );
    default:
      return alertText(alert);
  }
}

export function collectAlertBranches(summary) {
  return (summary?.branches || []).filter((branch) => branch.alerts && branch.alerts.length > 0);
}

/** 右上角使用者名稱旁：任一分店合約即將到期／已到期或點數不足時才出現 */
function BranchContractAlert() {
  const navigate = useNavigate();
  const { data } = useBranchContractSummary();
  const [open, setOpen] = useState(false);
  const branches = useMemo(() => collectAlertBranches(data), [data]);
  const tone = branches.some((b) => b.alerts.some((a) => SEVERITY[a.type] === "danger")) ? "danger" : "warn";

  if (branches.length === 0) return null;

  const goToBranch = (branch) => {
    setOpen(false);
    question(
      2,
      "前往分店合約頁面",
      `即將前往「${branch.branchName}」的合約與簡訊頁面，可能中斷目前正在進行的操作，確定要繼續嗎？`,
      "確定前往",
      "取消",
      () => {
        navigate(`/options/branch/${branch.branchId}`, { state: { scrollTo: "contract" } });
      }
    );
  };

  const panel = (
    <div className="branch-contract-alert__panel" role="menu">
      <p className="branch-contract-alert__panel-title">分店合約與簡訊提醒</p>
      <ul>
        {branches.map((branch) => (
          <li key={branch.branchId}>
            <button type="button" role="menuitem" onClick={() => goToBranch(branch)}>
              <span className="branch-contract-alert__branch">{branch.branchName}</span>
              <span className="branch-contract-alert__items">
                {branch.alerts.map((alert) => (
                  <span key={alert.type} className={`branch-contract-alert__item branch-contract-alert__item--${SEVERITY[alert.type]}`}>
                    {isSmsEmptyAlert(alert) ? (
                      <HiOutlineExclamationTriangle size={14} aria-hidden className="branch-contract-alert__item-icon" />
                    ) : null}
                    <AlertMessage alert={alert} />
                  </span>
                ))}
              </span>
              <HiChevronRight size={16} aria-hidden className="branch-contract-alert__chevron" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="branch-contract-alert me-2">
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={["click"]}
        placement="bottomRight"
        overlay={panel}
        overlayClassName="branch-contract-alert__dropdown"
        getPopupContainer={() => document.body}
      >
        <button
          type="button"
          className={`branch-contract-alert__trigger branch-contract-alert__trigger--${tone}`}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={`${branches.length} 間分店有合約或簡訊點數提醒`}
        >
          <HiOutlineBellAlert size={18} aria-hidden />
          <span className="branch-contract-alert__count tabular-nums">{branches.length}</span>
        </button>
      </Dropdown>
    </div>
  );
}

export default BranchContractAlert;
